/**
 * MobileSectionNav -- 모바일 하단 세그먼트 탭 + 라벨 그리드
 *
 * [콘텐츠] [설정] 세그먼트 탭 → 해당 카테고리의 아이콘+라벨 칩 가로 스크롤.
 */

import { type ReactNode, useState } from 'react'
import {
  MessageSquare, Users, Calendar, Image, Phone,
  CreditCard, UserCheck, BookOpen, Heart, Clock, Info,
  Type, Music, Share2, Settings,
} from 'lucide-react'
import { SECTION_LABELS, type SectionId } from '@cugumi/shared/schemas'
import { cn } from '../../lib/cn.js'
import {
  type AllSectionId,
  type SettingSectionId,
  SETTING_LABELS,
  SETTING_IDS,
} from './types.js'
import styles from './MobileSectionNav.module.css'

const CONTENT_ICONS: Partial<Record<SectionId, ReactNode>> = {
  greeting: <MessageSquare size={18} />,
  parents: <Users size={18} />,
  ceremony: <Calendar size={18} />,
  gallery: <Image size={18} />,
  contacts: <Phone size={18} />,
  accounts: <CreditCard size={18} />,
  rsvp: <UserCheck size={18} />,
  guestbook: <BookOpen size={18} />,
  timeline: <Heart size={18} />,
  schedule: <Clock size={18} />,
  notices: <Info size={18} />,
}

const SETTING_ICONS: Record<SettingSectionId, ReactNode> = {
  fontStyle: <Type size={18} />,
  bgm: <Music size={18} />,
  kakaoShare: <Share2 size={18} />,
  settings: <Settings size={18} />,
}

type TabId = 'content' | 'settings'

interface MobileSectionNavProps {
  activeSection: AllSectionId | null
  onSelect: (id: AllSectionId) => void
  sectionOrder: SectionId[]
}

export function MobileSectionNav({ activeSection, onSelect, sectionOrder }: MobileSectionNavProps) {
  const [tab, setTab] = useState<TabId>('content')

  return (
    <nav className={styles.root} aria-label="섹션 네비게이터">
      <div className={styles.tabs}>
        <button
          type="button"
          className={cn(styles.tab, tab === 'content' && styles.tabActive)}
          onClick={() => setTab('content')}
        >
          콘텐츠
        </button>
        <button
          type="button"
          className={cn(styles.tab, tab === 'settings' && styles.tabActive)}
          onClick={() => setTab('settings')}
        >
          설정
        </button>
      </div>

      <div className={styles.grid}>
        {tab === 'content'
          ? sectionOrder.map((id) => (
              <button
                key={id}
                type="button"
                className={cn(styles.chip, activeSection === id && styles.chipActive)}
                onClick={() => onSelect(id)}
              >
                <span className={styles.chipIcon}>{CONTENT_ICONS[id]}</span>
                {SECTION_LABELS[id]}
              </button>
            ))
          : SETTING_IDS.map((id) => (
              <button
                key={id}
                type="button"
                className={cn(styles.chip, activeSection === id && styles.chipActive)}
                onClick={() => onSelect(id)}
              >
                <span className={styles.chipIcon}>{SETTING_ICONS[id]}</span>
                {SETTING_LABELS[id]}
              </button>
            ))
        }
      </div>
    </nav>
  )
}
